import { EquipmentDTO, EventDTO, LocationDTO, RailResponseDTO, RouteDTO } from "../api/response.types";
import { EquipmentModel, EventModel, LocationModel, ResponseModel, RouteModel, TransformedEventModel } from "./types";
import { RailMessagesModel, RailStatusesModel } from "entities/shared/railCard.types";
import {
  CoordinateModel,
  DateTimeModel,
  SearchReferenceModel,
  ShipmentVariantModel,
} from "entities/shared/shipmentCard.types";
import { RouteDataType, TransportTypes } from "shared/api/lib/map.types";
import { MapRoute, MapRouteModel } from "entities/shared/mapRouteModel";
import { SubscriptionDataModel, SubscriptionModel } from "entities/shared/subscriptionModel";
import { BaseModel } from "shared/api/lib/baseModel";
import { isNullable } from "shared/lib/typeCheck.lib";
import { Nullable, NullableValues, ValueOf } from "shared/lib/typeCheck.lib";
import { getUtcTimestamp } from "shared/utils/dateTime.utils";
import { isAppIntegration } from "shared/utils/url.utils";

import { SearchApiParser } from "../api/parser";

export const isRailSearchShipmentModel = (model: Nullable<{ responseType: string }>): model is RailSearchCardModel =>
  model?.responseType === ShipmentVariantModel.RailSearch;

export class RailSearchCardModel extends BaseModel<SearchApiParser, RailResponseDTO> {
  public readonly responseType: ShipmentVariantModel;
  public readonly requestLimitReachedAppIntegration: boolean = false;
  public readonly requestLimitReachedWebApp: boolean = false;
  private readonly _subscription: SubscriptionModel | null = null;
  private _equipments: EquipmentModel[] = [];
  private _events: EventModel[] = [];
  private _routes: RouteModel[] = [];
  private _route: MapRoute | null = null;

  constructor(DTO: RailResponseDTO) {
    super({ apiParser: new SearchApiParser(DTO), DTO });

    this._subscription = new SubscriptionModel(this.apiParser.metadata);
    this.setEquipments();
    this.setEvents();
    this.setRoutes();
    this._route = new MapRoute(this.adaptRouteData());
    this.responseType = ShipmentVariantModel.RailSearch;
    this.requestLimitReachedAppIntegration = this.requestLimitReached && isAppIntegration;
    this.requestLimitReachedWebApp = this.requestLimitReached && !isAppIntegration;
  }

  get isSuccess(): boolean {
    return this.apiParser.isSuccess;
  }

  get requestLimitReached(): boolean {
    return this.apiParser.requestLimitReached;
  }

  get equipments(): EquipmentModel[] {
    return this._equipments;
  }

  get reference(): SearchReferenceModel {
    return this.apiParser.number;
  }

  get responseStatus(): ValueOf<typeof RailMessagesModel> {
    return this.apiParser.responseStatus;
  }

  get progress(): number {
    return this.calcProgress({
      startDate: this.atd ?? this.etd,
      endDate: this.ata ?? this.eta,
      status: this.status,
    });
  }

  get from(): Nullable<string> {
    return this.firstLocation?.formattedName;
  }

  get to(): Nullable<string> {
    return this.lastLocation?.formattedName;
  }

  get carrierCode(): string {
    return this.apiParser.carrierCode;
  }

  get routes(): RouteModel[] {
    return this._routes;
  }

  get events(): EventModel[] {
    return this._events;
  }

  public toJSON(): ResponseModel {
    return this.rawData;
  }

  public getTransformedEvents(targetEvents: Nullable<EventModel[]>): TransformedEventModel[] {
    if (!targetEvents) return [];

    const transformedEvents: TransformedEventModel[] = [{ location: null, events: [] }];

    const isSameLocation = (a: Nullable<LocationModel>, b: Nullable<LocationModel>): boolean => {
      if (!a || !b) return false;
      return a.name === b.name;
    };

    const formatLocationName = (location: Nullable<LocationModel>): Nullable<string> => {
      if (!location) return null;
      if (location.name && location.stationCode) {
        return `${location.name} (${location.stationCode})`;
      }
      return location.name;
    };

    let prevLocation: Nullable<LocationModel> = null;

    const events = this._equipments.flatMap((equipment) => equipment.events);
    events.forEach((event, i) => {
      if (i === 0) {
        transformedEvents[0].location = formatLocationName(event.location);
        transformedEvents[0].events.push(event);
      } else {
        if (isSameLocation(prevLocation, event.location)) {
          transformedEvents[transformedEvents.length - 1].events.push(event);
        } else {
          transformedEvents.push({ location: event.location?.name, events: [event] });
        }
      }

      prevLocation = event.location;
    });

    return transformedEvents;
  }

  public hasPin(): boolean {
    return (
      this.apiParser.responseStatus !== RailMessagesModel.RAILWAY_COMPANY_HASNT_PROVIDE_INFO &&
      this.apiParser.responseStatus !== RailMessagesModel.RAILWAY_COMPANY_NO_RESPONSE &&
      this.apiParser.responseStatus === RailMessagesModel.OK
    );
  }

  get loadingCoordinate(): CoordinateModel | null {
    return this.getCoordinate({
      lat: this.firstLocation?.lat,
      lng: this.firstLocation?.lng,
    });
  }

  get departureCoordinate(): CoordinateModel | null {
    return this.getCoordinate({
      lat: this.lastLocation?.lat,
      lng: this.lastLocation?.lng,
    });
  }

  get status(): ValueOf<typeof RailStatusesModel> {
    return this.firstEquipment?.status ?? RailStatusesModel.UNKNOWN;
  }

  get etd(): Nullable<DateTimeModel> {
    return this.firstEquipment?.etd;
  }

  get atd(): Nullable<DateTimeModel> {
    return this.firstEquipment?.atd;
  }

  get eta(): Nullable<DateTimeModel> {
    return this.lastEquipment?.eta;
  }

  get ata(): Nullable<DateTimeModel> {
    return this.lastEquipment?.ata;
  }

  get firstLocation(): LocationModel | null {
    if (!this.firstEquipment?.from) return null;
    return this.firstEquipment.from;
  }

  get equipmentsCount(): number {
    return this._equipments.length;
  }

  get firstEquipment(): EquipmentModel | null {
    return this.apiParser.equipments.length ? this.equipments[0] : null;
  }

  get lastEquipment(): EquipmentModel | null {
    if (!this._equipments.length) return null;
    return this._equipments.length ? this._equipments[this._equipments.length - 1] : null;
  }

  get lastLocation(): LocationModel | null {
    if (!this.lastEquipment?.to) return null;
    return this.lastEquipment.to;
  }

  private normalizeEquipment(equipment: EquipmentDTO): EquipmentModel {
    return {
      number: equipment.number,
      wagonNumber: equipment.wagon_number,
      trainNumber: equipment.train_number,
      status: equipment.status,
      from: equipment.from ? this.normalizeLocation(equipment.from) : null,
      to: equipment.to ? this.normalizeLocation(equipment.to) : null,
      atd: equipment.departure_datetime.actual,
      etd: equipment.departure_datetime.estimated,
      ata: equipment.arrival_datetime.actual,
      eta: equipment.arrival_datetime.estimated,
      events: equipment.events ? equipment.events.map((event) => this.normalizeEvent(event)) : [],
    };
  }

  private normalizeLocation(location: LocationDTO): LocationModel {
    const lat = isNullable(location.lat) ? location.lat : Number(location.lat);
    const lng = isNullable(location.lng) ? location.lng : Number(location.lng);

    return {
      name: location.name,
      division: location.division,
      state: location.state,
      country: location.country,
      countryCode: location.country_code,
      locode: location.locode,
      stationCode: location.station_code,
      railwayZone: location.railway_zone,
      lat,
      lng,
      formattedName: this.formatLocationName({
        country: location.country,
        countryCode: location.country_code,
      }),
    };
  }

  private normalizeEvent(event: EventDTO): EventModel {
    return {
      description: event.description,
      datetime: {
        estimated: event.datetime.estimated,
        actual: event.datetime.actual,
      },
      location: event.location ? this.normalizeLocation(event.location) : null,
      transport: event.transport_mode,
    };
  }

  private formatLocationName({
    country,
    countryCode,
  }: {
    country: Nullable<string>;
    countryCode: Nullable<string>;
  }): string {
    if (country && countryCode) {
      return `${country}, ${countryCode}`;
    }

    return country ?? countryCode ?? "";
  }

  private getCoordinate(coords: NullableValues<CoordinateModel>): CoordinateModel | null {
    if (!this.ensureCoordinate(coords)) return null;

    return {
      lat: coords.lat,
      lng: coords.lng,
    };
  }

  private ensureCoordinate(value: NullableValues<CoordinateModel>): value is CoordinateModel {
    return !isNullable(value?.lat) && !isNullable(value?.lng);
  }

  private setEquipments(): void {
    this._equipments = this.apiParser.equipments.map((equipment) => this.normalizeEquipment(equipment));
  }

  private setRoutes(): void {
    this._routes = this.apiParser.equipments.reduce<RouteModel[]>((acc, equipment) => {
      if (equipment.routes) {
        acc.push(...equipment.routes.map((route) => this.normalizeRoute(route)));
      }
      return acc;
    }, []);
  }

  private normalizeRoute(route: RouteDTO): RouteModel {
    return {
      from: this.normalizeLocation(route.from),
      to: this.normalizeLocation(route.to),
      atd: route.departure_datetime.actual,
      etd: route.departure_datetime.estimated,
      ata: route.arrival_datetime.actual,
      eta: route.arrival_datetime.estimated,
    };
  }

  private setEvents(): void {
    this._events = this.apiParser.equipments.reduce<EventModel[]>((acc, equipment) => {
      if (equipment.events) {
        acc.push(...equipment.events.map((event) => this.normalizeEvent(event)));
      }
      return acc;
    }, []);
  }

  private calcProgress({
    startDate,
    endDate,
    status,
  }: {
    startDate: Nullable<DateTimeModel>;
    endDate: Nullable<DateTimeModel>;
    status: ValueOf<typeof RailStatusesModel>;
  }): number {
    const notStartedStatusesSet = new Set<ValueOf<typeof RailStatusesModel>>([
      RailStatusesModel.BOOKED,
      RailStatusesModel.RECEIVED,
    ]);
    const completedStatusesSet = new Set<ValueOf<typeof RailStatusesModel>>([
      RailStatusesModel.NOTIFIED,
      RailStatusesModel.DELIVERED,
      RailStatusesModel.ARRIVED,
    ]);
    if (notStartedStatusesSet.has(status)) return 0;
    if (completedStatusesSet.has(status)) return 100;
    if (status === RailStatusesModel.CANCELLED) return 0;

    if (!startDate || !endDate || startDate === endDate) return 0;

    const start: number = getUtcTimestamp(startDate);
    const end: number = getUtcTimestamp(endDate);
    const today: number = getUtcTimestamp(new Date());

    const totalTime = Math.abs(end - start);
    const elapsedTime = Math.abs(today - start);

    let percentageElapsed: number;

    if (elapsedTime <= 0) {
      percentageElapsed = 0;
    } else if (elapsedTime >= totalTime) {
      percentageElapsed = 100;
    } else {
      percentageElapsed = Math.round((elapsedTime / totalTime) * 100);
    }

    return percentageElapsed;
  }

  get subscription(): SubscriptionDataModel | null {
    return this._subscription?.data ?? null;
  }

  private adaptRouteData(): MapRouteModel {
    const routes = this.routes ?? [];

    const waypoints = routes.flatMap((route, index) => {
      const isEdge = index === 0 || index === routes.length - 1;
      return isEdge ? [route.from, route.to] : [route.from];
    });

    return {
      route: waypoints.reduce<NonNullable<MapRouteModel["route"]>>((acc, wp, index) => {
        const next = waypoints[index + 1];
        if (!next) return acc;
        if (wp.lat == null || wp.lng == null || next.lat == null || next.lng == null) return acc;

        acc.push({
          path: [
            [wp.lat, wp.lng],
            [next.lat, next.lng],
          ],
          type: RouteDataType.LAND,
          transportType: TransportTypes.RAIL,
        });
        return acc;
      }, []),
      locations: waypoints.map((wp, index) => ({
        id: index,
        name: wp.name ?? "",
        country: wp.country ?? "",
        countryCode: wp.countryCode ?? "",
        locode: wp.locode,
        lat: wp.lat,
        lng: wp.lng,
      })),
    };
  }

  get route(): MapRoute | null {
    return this._route;
  }
}
